import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../config/firebase';
import useEventStore, {
    EventModel,
    getEventActiveDateRaw,
    normalizeEventTargetAudience,
    parseLocalEventDate,
    resolveEventRecording,
} from '../store/eventStore';
import useAppStore from '../store/appStore';
import useCoachingAppointmentStore, {
    isCoachingAppointmentUpcoming,
    parseCoachingLocalDateTime,
} from '../store/coachingAppointmentStore';
import EventLiveCountdownBadge from '../components/EventLiveCountdownBadge';
import AdaptiveEventImage from '../components/AdaptiveEventImage';
import EventRecordingBadge from '../components/EventRecordingBadge';
import EventMeetJoinButton from '../components/events/EventMeetJoinButton';
import CoachingMeetJoinButton from '../components/events/CoachingMeetJoinButton';
import { clarifyOnlineFormat, clarifyOnlineWording } from '../utils/onlineEventWording';

// --- ICONS ---
const IconBase = ({ children, size = 20, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const CalendarIcon = (props: any) => <IconBase {...props}><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></IconBase>;
const ClockIcon = (props: any) => <IconBase {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></IconBase>;
const ExternalLinkIcon = (props: any) => <IconBase {...props}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></IconBase>;
const VideoIcon = (props: any) => <IconBase {...props}><polygon points="23 7 16 12 23 17 23 7"/><rect width="15" height="14" x="1" y="5" rx="2" ry="2"/></IconBase>;

type EventSpeaker = { name: string; role?: string; bio?: string; imageUrl?: string };

const EVENT_SPEAKER_NAME_ALIASES: Record<string, string> = {
    'Martina Funk': 'Martina Jovanovic',
    Jean: 'Jean Standardt',
    'Jean Standard': 'Jean Standardt',
    Nelly: 'Nelly Rühle',
};

const normalizeSpeakerDisplayName = (name: string): string => {
    const trimmed = String(name || '').trim();
    return EVENT_SPEAKER_NAME_ALIASES[trimmed] || trimmed;
};

const decodeBasicHtmlEntities = (value: string): string =>
    String(value || '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'");

const stripHtmlToText = (html: string): string =>
    decodeBasicHtmlEntities(
        String(html || '')
            .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<[^>]*>/g, ' ')
    )
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();

const looksLikeHtml = (value: string): boolean => /<[a-z][\s\S]*>/i.test(String(value || ''));

/** Quill-HTML für Kalender: leere Absätze raus, enge Zeilen, Leerzeichen um Fettung. */
const normalizeQuillHtmlForDisplay = (html: string): string => {
    let out = String(html || '').trim();
    if (!out) return '';
    out = out.replace(/\r\n/g, '\n').replace(/\n+/g, '');
    // Leere Quill-Zeilen (<p><br></p>) = optisch 2–3 Leerzeilen
    out = out.replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '');
    out = out.replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br/>');
    out = out.replace(/([^\s>])(<(?:strong|b|em|i)\b[^>]*>)/gi, '$1 $2');
    out = out.replace(/(<\/(?:strong|b|em|i)>)([^\s<.,;:!?])/gi, '$1 $2');
    return out;
};

/** Quill-/HTML-Beschreibung ohne Tags-Zerstörung für die Anzeige klären. */
const clarifyOnlineHtml = (html: string): string => {
    const raw = String(html || '').trim();
    if (!raw) return '';
    if (!looksLikeHtml(raw)) return clarifyOnlineWording(raw);
    const clarified = raw.replace(/>([^<]+)</g, (_m, text: string) => `>${clarifyOnlineWording(text)}<`);
    return normalizeQuillHtmlForDisplay(clarified);
};

const resolveEventMarketing = (ev: EventModel) => {
    const rawDescription = String(ev.description || '').trim();
    const descriptionPlain = stripHtmlToText(rawDescription);
    const publicTeaser = String(ev.publicTeaser || '').trim();
    const descriptionIsHtml = looksLikeHtml(rawDescription);

    // Haupttext: öffentliche Teaser-Zeile ODER formatierte Monats-Beschreibung (HTML beibehalten)
    let teaser = '';
    let teaserIsHtml = false;
    if (publicTeaser) {
        teaserIsHtml = looksLikeHtml(publicTeaser);
        teaser = teaserIsHtml
            ? clarifyOnlineHtml(publicTeaser)
            : clarifyOnlineWording(publicTeaser);
    } else if (rawDescription) {
        teaserIsHtml = descriptionIsHtml;
        teaser = descriptionIsHtml
            ? clarifyOnlineHtml(rawDescription)
            : clarifyOnlineWording(descriptionPlain || rawDescription);
    }

    const extraDescription = rawDescription;
    const showExtraDescription = Boolean(
        descriptionPlain &&
            publicTeaser &&
            descriptionPlain.toLowerCase() !== stripHtmlToText(publicTeaser).toLowerCase()
    );
    const bullets = (Array.isArray(ev.publicBullets) ? ev.publicBullets : [])
        .map((item) => clarifyOnlineWording(String(item || '').trim()))
        .filter(Boolean);
    const speakers = (Array.isArray(ev.publicSpeakers) ? ev.publicSpeakers : [])
        .map((speaker) => ({
            name: normalizeSpeakerDisplayName(String(speaker?.name || '').trim()),
            role: speaker?.role ? String(speaker.role).trim() : undefined,
            bio: speaker?.bio ? String(speaker.bio).trim() : undefined,
            imageUrl: speaker?.imageUrl ? String(speaker.imageUrl).trim() : undefined,
        }))
        .filter((speaker): speaker is EventSpeaker => Boolean(speaker.name));
    const formatLabel = ev.publicFormat || ev.eventType
        ? clarifyOnlineFormat(ev.publicFormat || ev.eventType)
        : '';
    const isSingleEvent = ev.isRecurring !== true;
    const showLandingCopy = isSingleEvent || !!ev.isPublicOnlineEvent;
    const showMonthlyDecor = ev.isRecurring === true && !!(ev.currentTopic || ev.imageUrl);
    return {
        teaser,
        teaserIsHtml,
        showExtraDescription,
        extraDescription,
        bullets,
        speakers,
        formatLabel,
        showLandingCopy,
        showMonthlyDecor,
    };
};

const EventSpeakersBlock: React.FC<{
    speakers: EventSpeaker[];
    showTeamFallback: boolean;
    compact?: boolean;
    extraTile?: React.ReactNode;
}> = ({ speakers, showTeamFallback, compact, extraTile }) => {
    const list: EventSpeaker[] = speakers.length > 0
        ? speakers
        : showTeamFallback
            ? [{ name: 'Das PreisExpert Team', role: 'Praxisnah · auf Augenhöhe' }]
            : [];
    if (!list.length && !extraTile) return null;

    if (compact) {
        if (!list.length) return null;
        return (
            <div className="mb-4">
                <span className="text-[9px] font-black uppercase tracking-widest text-[#E6C299] block mb-1">Referenten</span>
                <div className="space-y-1">
                    {list.map((speaker) => (
                        <p key={speaker.name} className="text-[11px] text-pe-muted dark:text-slate-400 leading-snug">
                            {speaker.name}
                            {speaker.role ? (
                                <span className="text-[#E6C299]"> · {speaker.role}</span>
                            ) : null}
                        </p>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-2xl mb-8">
            {list.length > 0 && (
                <p className="text-[10px] font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] mb-3">
                    Referenten
                </p>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
                {list.map((speaker) => (
                    <div
                        key={speaker.name}
                        className="flex gap-3 p-3 rounded-xl border border-pe-border dark:border-slate-800 bg-pe-elevated dark:bg-[#15171e]"
                    >
                        {speaker.imageUrl ? (
                            <img
                                src={speaker.imageUrl}
                                alt={speaker.name}
                                className="w-12 h-12 rounded-full object-cover border border-slate-700 shrink-0"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-black shrink-0 border border-[#E6C299]/40 text-[#E6C299] bg-[#08090b]">
                                {speaker.name === 'Das PreisExpert Team' ? 'PE' : speaker.name.charAt(0)}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-pe-text dark:text-white">{speaker.name}</p>
                            {speaker.role && (
                                <p className="text-[10px] uppercase tracking-wider text-[#E6C299] mt-0.5">{speaker.role}</p>
                            )}
                            {speaker.bio && (
                                <p className="text-xs text-pe-muted dark:text-slate-400 mt-1 leading-relaxed">{speaker.bio}</p>
                            )}
                        </div>
                    </div>
                ))}
                {extraTile}
            </div>
        </div>
    );
};

const diagnoseEventImageError = (
    imageType: 'title' | 'topic',
    src: string,
    eventTitle?: string
) => {
    const tag = imageType === 'title'
        ? '[EventDashboard][TITELBILD]'
        : '[EventDashboard][THEMENBILD]';

    console.error(`${tag} onError — Bild konnte nicht geladen werden`, {
        src,
        eventTitle: eventTitle ?? '(unbekannt)',
    });

    fetch(src, { method: 'HEAD' })
        .then((response) => {
            let diagnosis: string;
            if (response.status === 404) {
                diagnosis = '404 Not Found — Bild-URL zeigt auf nicht existierende Datei';
            } else if (response.status === 403) {
                diagnosis = '403 Forbidden — Firebase Storage Rules oder abgelaufener Token';
            } else if (!response.ok) {
                diagnosis = `HTTP ${response.status} ${response.statusText}`;
            } else {
                diagnosis = `HEAD ${response.status} OK — img onError trotzdem (Format/Decode?)`;
            }

            console.error(`${tag} fetch(HEAD) Ergebnis`, {
                src,
                status: response.status,
                statusText: response.statusText,
                diagnosis,
                eventTitle: eventTitle ?? '(unbekannt)',
            });
        })
        .catch((error) => {
            console.error(`${tag} fetch(HEAD) fehlgeschlagen`, {
                src,
                error,
                hint: 'Netzwerkfehler oder CORS-Blockade beim fetch (img onError kann trotzdem 403/404 sein)',
                eventTitle: eventTitle ?? '(unbekannt)',
            });
        });
};

const resolveViewerClientId = (): string => {
    try {
        const god = sessionStorage.getItem('GOD_MODE_ID');
        if (god && String(god).trim()) return String(god).trim();
    } catch {
        /* ignore */
    }
    return String(auth.currentUser?.uid || '').trim();
};

const EventDashboard: React.FC = () => {
    const { events, fetchEvents, isLoading } = useEventStore();
    const { clientData } = useAppStore();
    const {
        appointments: coachingAppointments,
        fetchByClientId: fetchCoachingAppointments,
        isLoading: coachingLoading,
    } = useCoachingAppointmentStore();
    const [focusEventId, setFocusEventId] = useState<string | null>(null);
    const [viewerClientId, setViewerClientId] = useState(() => resolveViewerClientId());

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    useEffect(() => {
        const syncViewer = () => setViewerClientId(resolveViewerClientId());
        syncViewer();
        const unsub = auth.onAuthStateChanged(() => syncViewer());
        return () => unsub();
    }, []);

    useEffect(() => {
        if (viewerClientId) fetchCoachingAppointments(viewerClientId);
    }, [viewerClientId, fetchCoachingAppointments]);

    useEffect(() => {
        try {
            const id = sessionStorage.getItem('peone_focus_event_id');
            if (id) {
                setFocusEventId(id);
                sessionStorage.removeItem('peone_focus_event_id');
            }
        } catch {
            /* ignore */
        }
    }, []);

    const upcomingCoaching = useMemo(() => {
        return coachingAppointments
            .filter((a) => isCoachingAppointmentUpcoming(a))
            .slice()
            .sort((a, b) => {
                const ta = parseCoachingLocalDateTime(a.datum, a.uhrzeit)?.getTime() ?? Number.MAX_SAFE_INTEGER;
                const tb = parseCoachingLocalDateTime(b.datum, b.uhrzeit)?.getTime() ?? Number.MAX_SAFE_INTEGER;
                return ta - tb;
            });
    }, [coachingAppointments]);

    const filteredEvents = useMemo(() => {
        const isInsider = clientData?.insiderTeamleaderStandardChecked || clientData?.insiderTeamleaderPlusChecked;
        const isBasic = clientData?.basicChecked || isInsider;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return events.filter((ev) => {
            const audience = normalizeEventTargetAudience(ev.targetAudience);
            // Leer: bisheriges Default (sichtbar für alle Lizenz-Stufen)
            const effectiveAudience = audience.length > 0 ? audience : ['external', 'extern'];
            const isMandantUser = isBasic || isInsider;
            const audienceMatch =
                effectiveAudience.includes('external') ||
                effectiveAudience.includes('extern') ||
                (isMandantUser &&
                    (effectiveAudience.includes('mandanten') ||
                        effectiveAudience.includes('basic') ||
                        effectiveAudience.includes('team'))) ||
                (isInsider &&
                    (effectiveAudience.includes('insider') || effectiveAudience.includes('intern')));

            if (!audienceMatch) return false;

            const isRecurring = ev.isRecurring === true;
            if (isRecurring) return true;

            const activeDate = parseLocalEventDate(getEventActiveDateRaw(ev));
            if (!activeDate) return false;

            return activeDate >= today;
        });
    }, [events, clientData]);

    const orderedEvents = useMemo(() => {
        if (!focusEventId) return filteredEvents;
        const idx = filteredEvents.findIndex((ev) => ev.id === focusEventId);
        if (idx <= 0) return filteredEvents;
        const next = [...filteredEvents];
        const [focused] = next.splice(idx, 1);
        return [focused, ...next];
    }, [filteredEvents, focusEventId]);

    const heroEvent = orderedEvents.length > 0 ? orderedEvents[0] : null;
    const upcomingEvents = orderedEvents.slice(1);
    const heroMarketing = heroEvent ? resolveEventMarketing(heroEvent) : null;
    const heroDate = heroEvent ? parseLocalEventDate(getEventActiveDateRaw(heroEvent)) : null;
    const heroRecording = heroEvent ? resolveEventRecording(heroEvent) : null;

    // Chronologie: 1:1 vor Gruppen-Event, wenn es früher startet
    const nextCoachingAt = upcomingCoaching[0]
        ? parseCoachingLocalDateTime(upcomingCoaching[0].datum, upcomingCoaching[0].uhrzeit)
        : null;
    const nextGroupAt = (() => {
        if (!heroEvent || !heroDate) return null;
        const time = String(heroEvent.timeMorning || '00:00').trim();
        const m = time.match(/^(\d{1,2}):(\d{2})$/);
        const d = new Date(heroDate);
        d.setHours(m ? Number(m[1]) : 0, m ? Number(m[2]) : 0, 0, 0);
        return d;
    })();
    const coachingComesFirst =
        Boolean(nextCoachingAt) &&
        (!nextGroupAt || (nextCoachingAt as Date).getTime() <= nextGroupAt.getTime());

    const renderCoachingSection = (opts?: { asNext?: boolean; className?: string }) => {
        if (!(upcomingCoaching.length > 0 || coachingLoading)) return null;
        const asNext = Boolean(opts?.asNext);
        return (
            <div className={`max-w-6xl mx-auto px-6 animate-fade-in ${opts?.className || ''}`}>
                <div className="flex flex-wrap items-center gap-3 mb-6 border-l-4 border-emerald-400 pl-4">
                    <h3 className="text-xl font-black text-pe-text dark:text-white uppercase tracking-widest">
                        {asNext ? 'Nächster Termin · Coaching' : 'Deine Coaching-Termine'}
                    </h3>
                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400/90 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded">
                        1:1
                    </span>
                </div>
                {coachingLoading && upcomingCoaching.length === 0 ? (
                    <p className="text-sm text-pe-muted animate-pulse">Lade Coaching-Termine…</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {upcomingCoaching.map((ap, idx) => {
                            const dt = parseCoachingLocalDateTime(ap.datum, ap.uhrzeit);
                            const isLead = asNext && idx === 0;
                            return (
                                <div
                                    key={ap.id}
                                    className={`rounded-2xl border p-5 flex gap-4 items-start transition-colors ${
                                        isLead
                                            ? 'md:col-span-2 border-emerald-400/50 bg-emerald-500/10 shadow-[0_0_30px_rgba(16,185,129,0.12)]'
                                            : 'border-emerald-500/25 bg-pe-card dark:bg-[#0f1015] hover:border-emerald-400/50'
                                    }`}
                                >
                                    {ap.coachAvatarUrl ? (
                                        <img
                                            src={ap.coachAvatarUrl}
                                            alt={ap.coachName}
                                            className="w-12 h-12 rounded-full object-cover border border-pe-border shrink-0"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-400 font-black shrink-0">
                                            {(ap.coachName || 'C').slice(0, 1).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        {isLead && (
                                            <span className="inline-flex mb-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">
                                                Als Nächstes
                                            </span>
                                        )}
                                        <div className="font-black text-pe-text dark:text-white tracking-tight text-lg">
                                            {ap.title}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs font-mono text-pe-muted">
                                            <span className="inline-flex items-center gap-1 bg-pe-elevated dark:bg-[#15171e] px-2.5 py-1 rounded-lg border border-pe-border dark:border-slate-800">
                                                <CalendarIcon size={12} className="text-emerald-400" />
                                                {dt?.toLocaleDateString('de-DE', {
                                                    weekday: 'short',
                                                    day: '2-digit',
                                                    month: 'long',
                                                    year: 'numeric',
                                                }) ?? ap.datum}
                                            </span>
                                            <span className="inline-flex items-center gap-1 bg-pe-elevated dark:bg-[#15171e] px-2.5 py-1 rounded-lg border border-pe-border dark:border-slate-800">
                                                <ClockIcon size={12} className="text-emerald-400" />
                                                {ap.uhrzeit} Uhr
                                                {ap.dauerMinuten ? ` · ${ap.dauerMinuten} Min.` : ''}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-pe-muted mt-2">
                                            mit <span className="text-[#E6C299] font-semibold">{ap.coachName}</span>
                                        </p>
                                        {ap.meetingLink && (
                                            <CoachingMeetJoinButton
                                                meetUrl={ap.meetingLink}
                                                startAt={dt}
                                                timeLabel={ap.uhrzeit}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-pe-bg dark:bg-[#08090b] flex items-center justify-center">
                <div className="animate-pulse text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-widest text-xl">Lade Events...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-pe-bg dark:bg-[#08090b] text-pe-text dark:text-slate-200 pb-24 font-sans lg:pr-36">

            {/* 1:1 zuerst, wenn es früher startet als das nächste Gruppen-Event */}
            {coachingComesFirst && renderCoachingSection({ asNext: true, className: 'pt-12 md:pt-16' })}
            
            {/* HERO SECTION — mittig, Abstand zur rechten Button-Leiste */}
            {heroEvent && heroMarketing ? (
                <div className="relative w-full bg-pe-card dark:bg-[#0f1015] border-b border-pe-border dark:border-slate-800 pt-12 pb-16 md:py-20 px-6 md:px-10 overflow-hidden">
                    <div className="max-w-6xl mx-auto mb-6">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-pe-accent-text dark:text-[#E6C299]">
                            Kalender/Events
                        </p>
                    </div>
                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

                        {/* TEXT: Überschrift oben, Bild steht daneben oben — nicht vertikal mittig */}
                        <div className="lg:col-span-6 flex flex-col items-start z-10 order-2 lg:order-1">
                            <div className="flex flex-wrap items-center gap-3 mb-6">
                                <span className="bg-[#E6C299]/10 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/30 px-3 py-1 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                    <CalendarIcon size={14} /> Nächstes Online-Event
                                </span>
                                {heroEvent.isPaidEvent && (
                                    <span className="bg-red-500/10 text-red-400 border border-red-500/30 px-3 py-1 rounded text-[10px] font-black uppercase tracking-widest">
                                        Ticket erforderlich
                                    </span>
                                )}
                            </div>
                            
                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-pe-text dark:text-white leading-tight mb-2 tracking-tight">
                                {heroEvent.title}
                            </h1>
                            
                            {heroEvent.subtitle && (
                                <p className={`text-pe-muted dark:text-slate-400 mb-6 max-w-2xl whitespace-pre-wrap ${heroMarketing.showLandingCopy ? 'text-lg md:text-xl leading-relaxed font-medium not-italic' : 'text-xl md:text-2xl font-bold italic'}`}>
                                    {clarifyOnlineWording(heroEvent.subtitle)}
                                </p>
                            )}

                            {heroMarketing.showLandingCopy ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 w-full max-w-2xl">
                                    {heroDate && (
                                        <div className="rounded-xl border border-pe-border dark:border-slate-700 p-4 bg-pe-elevated dark:bg-[#15171e]">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Wochentag</p>
                                            <p className="text-sm font-bold text-pe-text dark:text-white">
                                                {heroDate.toLocaleDateString('de-DE', { weekday: 'long' })}
                                            </p>
                                        </div>
                                    )}
                                    <div className="rounded-xl border border-pe-border dark:border-slate-700 p-4 bg-pe-elevated dark:bg-[#15171e]">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                                            <CalendarIcon size={12} className="text-[#E6C299]" /> Datum
                                        </p>
                                        <p className="text-sm font-bold text-pe-text dark:text-white">
                                            {heroDate?.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) ?? '—'}
                                        </p>
                                    </div>
                                    <div className="rounded-xl border border-pe-border dark:border-slate-700 p-4 bg-pe-elevated dark:bg-[#15171e]">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                                            <ClockIcon size={12} className="text-[#E6C299]" /> Uhrzeit
                                        </p>
                                        <p className="text-sm font-bold text-pe-text dark:text-white">
                                            {heroEvent.timeMorning ?? '—'} Uhr
                                            {heroEvent.hasDoubleSession && !heroEvent.isPaidEvent ? ` & ${heroEvent.timeEvening ?? ''} Uhr` : ''}
                                        </p>
                                    </div>
                                    {heroMarketing.formatLabel && (
                                        <div className="rounded-xl border border-pe-border dark:border-slate-700 p-4 bg-pe-elevated dark:bg-[#15171e]">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                                                <VideoIcon size={12} className="text-[#E6C299]" /> Online
                                            </p>
                                            <p className="text-sm font-bold text-pe-text dark:text-white">
                                                {heroMarketing.formatLabel.replace(/^online[\s·-]+/i, '').trim() || heroMarketing.formatLabel}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex flex-wrap items-center gap-4 text-sm font-mono text-pe-muted dark:text-slate-300 mb-8">
                                    <span className="flex items-center gap-2 bg-pe-elevated dark:bg-[#15171e] px-4 py-2 rounded-lg border border-pe-border dark:border-slate-800">
                                        <CalendarIcon size={16} className="text-pe-accent-text dark:text-[#E6C299]"/> 
                                        {heroDate?.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) ?? '—'}
                                    </span>
                                    <span className="flex items-center gap-2 bg-pe-elevated dark:bg-[#15171e] px-4 py-2 rounded-lg border border-pe-border dark:border-slate-800">
                                        <ClockIcon size={16} className="text-pe-accent-text dark:text-[#E6C299]"/> 
                                        {heroEvent.timeMorning ?? '—'} Uhr
                                        {heroEvent.hasDoubleSession && !heroEvent.isPaidEvent && ` & ${heroEvent.timeEvening ?? ''} Uhr`}
                                    </span>
                                </div>
                            )}

                            {(heroMarketing.speakers.length > 0 || heroMarketing.showLandingCopy || typeof heroRecording === 'boolean') && (
                                <EventSpeakersBlock
                                    speakers={heroMarketing.speakers}
                                    showTeamFallback={heroMarketing.showLandingCopy}
                                    extraTile={
                                        typeof heroRecording === 'boolean' ? (
                                            <EventRecordingBadge recorded={heroRecording} variant="tile" />
                                        ) : null
                                    }
                                />
                            )}

                            {heroMarketing.teaser && (
                                <div className="max-w-2xl mb-8 w-full">
                                    {heroMarketing.teaserIsHtml ? (
                                        <div
                                            className={`event-desc-html text-pe-muted dark:text-slate-300 leading-snug ${
                                                heroMarketing.showLandingCopy
                                                    ? 'text-base md:text-lg'
                                                    : 'text-sm md:text-base'
                                            } [&_p]:m-0 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_br]:leading-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:mb-1 [&_strong]:font-bold [&_em]:italic [&_a]:text-[#E6C299] [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-2`}
                                            dangerouslySetInnerHTML={{ __html: heroMarketing.teaser }}
                                        />
                                    ) : (
                                        <p
                                            className={`text-pe-muted dark:text-slate-300 leading-relaxed whitespace-pre-wrap ${
                                                heroMarketing.showLandingCopy
                                                    ? 'text-base md:text-lg'
                                                    : 'text-sm md:text-base'
                                            }`}
                                        >
                                            {heroMarketing.teaser}
                                        </p>
                                    )}
                                </div>
                            )}

                            {heroMarketing.showExtraDescription && !heroMarketing.showLandingCopy && (
                                <div className="max-w-2xl mb-6 w-full">
                                    {looksLikeHtml(heroMarketing.extraDescription) ? (
                                        <div
                                            className="event-desc-html text-pe-muted dark:text-slate-400 text-sm leading-snug [&_p]:m-0 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:mb-1 [&_strong]:font-bold [&_em]:italic [&_a]:text-[#E6C299]"
                                            dangerouslySetInnerHTML={{
                                                __html: clarifyOnlineHtml(heroMarketing.extraDescription),
                                            }}
                                        />
                                    ) : (
                                        <p className="text-pe-muted dark:text-slate-400 text-sm leading-relaxed whitespace-pre-wrap">
                                            {heroMarketing.extraDescription}
                                        </p>
                                    )}
                                </div>
                            )}

                            {heroMarketing.showLandingCopy && heroMarketing.bullets.length > 0 && (
                                <div className="max-w-2xl mb-8 w-full">
                                    <p className="text-sm font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] mb-4">
                                        Das erwartet dich
                                    </p>
                                    <ul className="space-y-3">
                                        {heroMarketing.bullets.map((bullet, idx) => (
                                            <li key={`${idx}-${bullet.slice(0, 24)}`} className="flex items-start gap-3 text-sm md:text-base text-pe-muted dark:text-slate-300">
                                                <span className="text-[#E6C299] shrink-0 mt-0.5" aria-hidden>✦</span>
                                                <span>{bullet}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Monatliche Meetings: Extra-Ausschmückung (Thema + Themenbild) */}
                            {heroMarketing.showMonthlyDecor && (
                                <div className="bg-pe-elevated dark:bg-slate-800/50 border-l-4 border-[#E6C299] p-4 sm:p-5 rounded-r-lg mb-8 w-full max-w-xl">
                                    <span className="text-[10px] text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-widest block mb-1">Monats-Thema</span>
                                    {heroEvent.currentTopic && (
                                        <h2 className="text-lg md:text-xl font-bold text-pe-text dark:text-white mb-4">
                                            {heroEvent.currentTopic}
                                        </h2>
                                    )}
                                    {heroEvent.imageUrl && (
                                        <a
                                            href={heroEvent.imageUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block w-full max-w-[360px] mx-auto sm:mx-0 rounded-xl overflow-hidden border border-[#E6C299]/35 shadow-xl hover:border-[#E6C299]/80 hover:scale-[1.01] transition-all bg-pe-sunken dark:bg-[#050505]"
                                            title="Themabild in voller Größe öffnen"
                                        >
                                            <img
                                                src={heroEvent.imageUrl}
                                                alt="Themabild"
                                                className="w-full h-auto object-contain block"
                                                onError={(e) => {
                                                    diagnoseEventImageError('topic', e.currentTarget.src, heroEvent.title);
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        </a>
                                    )}
                                    {heroEvent.imageUrl && (
                                        <p className="mt-2 text-[9px] text-stone-500 dark:text-slate-500 uppercase tracking-widest">Themabild · Tippen zum Vergrößern</p>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-wrap gap-4 w-full sm:w-auto">
                                {heroEvent.isPaidEvent ? (
                                    <button onClick={() => window.open(heroEvent.registrationUrl, '_blank')} className="w-full sm:w-auto bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest transition-all shadow-[0_0_30px_rgba(230,194,153,0.2)] flex items-center justify-center gap-3">
                                        <ExternalLinkIcon size={20} /> Zur Anmeldung
                                    </button>
                                ) : (
                                    <>
                                        {heroEvent.linkMorning && (
                                            <EventMeetJoinButton
                                                event={heroEvent}
                                                meetUrl={heroEvent.linkMorning}
                                                variant="hero"
                                                sessionHint={heroEvent.hasDoubleSession ? '(Morgens)' : ''}
                                            />
                                        )}
                                        {heroEvent.hasDoubleSession && heroEvent.linkEvening && (
                                            <EventMeetJoinButton
                                                event={heroEvent}
                                                meetUrl={heroEvent.linkEvening}
                                                useEvening
                                                variant="hero"
                                                sessionHint="(Abend)"
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Bild oben neben der Überschrift, nicht in der Mitte der Textspalte */}
                        <div className="lg:col-span-6 w-full z-10 order-1 lg:order-2 flex justify-center lg:justify-start lg:pt-1 self-start">
                            <AdaptiveEventImage
                                src={heroEvent.titleImage}
                                alt={heroEvent.title}
                                overlay={<EventLiveCountdownBadge event={heroEvent} />}
                                onErrorDiagnose={(src) => diagnoseEventImageError('title', src, heroEvent.title)}
                            />
                        </div>

                    </div>
                </div>
            ) : (
                <div className="pt-32 pb-16 px-6 text-center border-b border-pe-border dark:border-slate-800">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-pe-accent-text dark:text-[#E6C299] mb-4">
                        Kalender/Events
                    </p>
                    <CalendarIcon size={64} className="mx-auto text-slate-700 mb-6" />
                    <h1 className="text-3xl font-black text-pe-text dark:text-white tracking-widest uppercase">Derzeit keine Events geplant</h1>
                    <p className="text-stone-500 dark:text-slate-500 mt-2">Komm später wieder, um neue Termine zu entdecken.</p>
                </div>
            )}

            {/* 1:1 nach dem Gruppen-Hero, wenn das Gruppen-Event früher ist */}
            {!coachingComesFirst && renderCoachingSection({ className: 'mt-14' })}

            {/* UPCOMING EVENTS LIST */}
            {upcomingEvents.length > 0 && (
                <div className="max-w-6xl mx-auto px-6 mt-16 animate-fade-in">
                    <div className="flex items-center gap-3 mb-8 border-l-4 border-[#E6C299] pl-4">
                        <h3 className="text-xl font-black text-pe-text dark:text-white uppercase tracking-widest">Weitere Termine</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {upcomingEvents.map((ev) => {
                            const dateObj = parseLocalEventDate(getEventActiveDateRaw(ev));
                            const marketing = resolveEventMarketing(ev);
                            const recording = resolveEventRecording(ev);
                            
                            return (
                                <div key={ev.id} className="bg-pe-card dark:bg-[#0f1015] rounded-2xl border border-pe-border dark:border-slate-800 overflow-hidden hover:border-pe-border-strong dark:hover:border-slate-600 transition-all hover:-translate-y-1 hover:shadow-2xl group flex flex-col">
                                    <div className="relative bg-pe-elevated dark:bg-slate-900 border-b border-pe-border dark:border-slate-800 p-4 flex justify-center">
                                        <AdaptiveEventImage
                                            src={ev.titleImage}
                                            alt={ev.title}
                                            variant="card"
                                            overlay={<EventLiveCountdownBadge event={ev} />}
                                            onErrorDiagnose={(src) => diagnoseEventImageError('title', src, ev.title)}
                                        />
                                        <div className="absolute top-4 right-4 bg-black/80 backdrop-blur border border-slate-700 text-[#E6C299] px-3 py-1.5 rounded-lg text-xs font-black font-mono">
                                            {dateObj?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) ?? '—'}
                                        </div>
                                    </div>
                                    
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="mb-auto">
                                            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                                                <div className="flex items-center gap-2 min-w-0">
                                                {ev.isPaidEvent && (
                                                    <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest">
                                                        Ticket
                                                    </span>
                                                )}
                                                <span className="bg-pe-elevated dark:bg-slate-800 text-pe-muted dark:text-slate-300 border border-pe-border dark:border-slate-700 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                                                    <ClockIcon size={10} /> {ev.timeMorning ?? '—'} {ev.hasDoubleSession && !ev.isPaidEvent ? `& ${ev.timeEvening ?? ''}` : ''}
                                                </span>
                                                </div>
                                                {typeof recording === 'boolean' && (
                                                    <EventRecordingBadge recorded={recording} variant="chip" />
                                                )}
                                            </div>
                                            
                                            <h3 className="text-xl font-bold text-pe-text dark:text-white mb-1">{ev.title}</h3>
                                            
                                            {ev.subtitle && (
                                                <h4 className={`text-stone-500 dark:text-slate-500 mb-3 whitespace-pre-wrap ${marketing.showLandingCopy ? 'text-sm font-medium not-italic' : 'text-xs font-bold italic'}`}>{clarifyOnlineWording(ev.subtitle)}</h4>
                                            )}

                                            {(marketing.speakers.length > 0 || marketing.showLandingCopy) && (
                                                <EventSpeakersBlock
                                                    speakers={marketing.speakers}
                                                    showTeamFallback={marketing.showLandingCopy}
                                                    compact
                                                />
                                            )}

                                            {marketing.showMonthlyDecor ? (
                                                <div className="text-sm font-bold text-pe-accent-text dark:text-[#E6C299] mb-4 bg-[#E6C299]/5 p-3 rounded border border-[#E6C299]/10">
                                                    <div className="flex gap-3 items-start">
                                                        <div className="flex-1 min-w-0">
                                                            {ev.currentTopic && (
                                                                <div className="line-clamp-2">Thema: {ev.currentTopic}</div>
                                                            )}
                                                        </div>
                                                        {ev.imageUrl && (
                                                            <a
                                                                href={ev.imageUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-[#E6C299]/30"
                                                                title="Themabild"
                                                            >
                                                                <img
                                                                    src={ev.imageUrl}
                                                                    alt="Themabild"
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        diagnoseEventImageError('topic', e.currentTarget.src, ev.title);
                                                                        e.currentTarget.style.display = 'none';
                                                                    }}
                                                                />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                        
                                        <div className="pt-4 border-t border-pe-border dark:border-slate-800 mt-4">
                                            {ev.isPaidEvent ? (
                                                <button onClick={() => window.open(ev.registrationUrl, '_blank')} className="w-full bg-[#E6C299]/10 hover:bg-[#E6C299] text-pe-accent-text dark:text-[#E6C299] hover:text-black border border-[#E6C299]/30 py-3 rounded-lg font-bold uppercase tracking-widest text-[10px] transition-colors flex items-center justify-center gap-2">
                                                    <ExternalLinkIcon size={14} /> Anmeldung
                                                </button>
                                            ) : (
                                                <div className="flex gap-2">
                                                    {ev.linkMorning && (
                                                        <EventMeetJoinButton
                                                            event={ev}
                                                            meetUrl={ev.linkMorning}
                                                            variant="card"
                                                            sessionHint={ev.hasDoubleSession ? '(Vormittag)' : ''}
                                                        />
                                                    )}
                                                    {ev.hasDoubleSession && ev.linkEvening && (
                                                        <EventMeetJoinButton
                                                            event={ev}
                                                            meetUrl={ev.linkEvening}
                                                            useEvening
                                                            variant="card"
                                                            sessionHint="(Abend)"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventDashboard;
